import { A_BUILD_CHANGED, OPERATIONS_ARE_NOT_ACTIVITY } from "@rally/agreed";
import { useEffect, useRef } from "react";
import { askWhatIsDeployed } from "../../update.ts";

export type Notice = {
  seq: string;
  kind: string;
  conversation: string | null;
  project: string | null;
};

const FIRST_WAIT = 500;
const LONGEST_WAIT = 15_000;

let socket: WebSocket | null = null;
let waiting: ReturnType<typeof setTimeout> | null = null;
let wait = FIRST_WAIT;

let seen: string | null = null;
const readers = new Set<(notice: Notice) => void>();

function address(): string {
  const where = new URL(window.location.href);
  where.protocol = where.protocol === "https:" ? "wss:" : "ws:";
  where.pathname = "/r/live";
  where.search = seen ? `?since=${seen}` : "";
  return where.toString();
}

function connect(): void {
  if (socket || readers.size === 0) return;
  const open = new WebSocket(address());
  socket = open;

  open.onmessage = (event) => {
    let notice: Notice;
    try {
      notice = JSON.parse(String(event.data)) as Notice;
    } catch {
      return;
    }
    takeFrame(notice);
  };

  open.onopen = () => {
    wait = FIRST_WAIT;
  };

  open.onclose = () => {
    if (socket === open) socket = null;
    if (readers.size === 0) return;

    waiting = setTimeout(connect, wait);
    wait = Math.min(wait * 2, LONGEST_WAIT);
  };
}

export function takeFrame(notice: Notice): "asked what the build is" | "handed on" | "ignored" {
  if (!/^\d+$/.test(notice.seq)) return "ignored";
  const next = BigInt(notice.seq);
  const last = seen === null ? null : BigInt(seen);
  if (notice.kind === A_BUILD_CHANGED) {
    if (last === null || next > last) seen = notice.seq;
    void askWhatIsDeployed();
    return "asked what the build is";
  }

  if (last !== null && next <= last) return "ignored";
  seen = notice.seq;

  for (const reader of [...readers]) reader(notice);
  return "handed on";
}

function stopIfNobodyIsListening(): void {
  if (readers.size > 0) return;
  if (waiting !== null) clearTimeout(waiting);
  waiting = null;
  socket?.close();
  socket = null;
}

export type LiveWanted = "activity" | "everything";

export function useLive(told: (notice: Notice) => void, wanted: LiveWanted = "activity"): void {
  const latest = useRef(told);
  latest.current = told;

  useEffect(() => {
    const reader = (notice: Notice) => {
      if (wanted === "activity" && (OPERATIONS_ARE_NOT_ACTIVITY as readonly string[]).includes(notice.kind))
        return;
      latest.current(notice);
    };
    readers.add(reader);
    connect();
    return () => {
      readers.delete(reader);
      stopIfNobodyIsListening();
    };
  }, [wanted]);
}
